import type { User } from "firebase/auth";
import { CHARACTER_ACTIONS, type CharacterActionKey } from "@/character/actions";
import {
  createEmptyDraft,
  defaultGridTuning,
  gridTuningForImageSize,
  isDraftComplete,
  type CharacterDraft,
} from "@/character/draft";
import { readImageFileAsDataUrl } from "@/character/frame";
import {
  GRID_COLUMNS,
  GRID_ROWS,
  TEMPLATE_SHEET_HEIGHT,
  TEMPLATE_SHEET_WIDTH,
} from "@/character/grid";
import { sheetLayoutFromImage, supportsTemplateGridLayout } from "@/character/grid";
import { parseCharacterSpritesheet } from "@/character/spritesheet";
import { loadCharacterProfile, saveCharacterProfile } from "@/character/storage";
import { loadGameAccessMode, userCanAccessGmMode } from "@/game/game-mode";
import { permissionsForGameMode } from "@/game/game-mode-permissions";
import {
  applySheetUploadClean,
  bindSheetUploadCleanControls,
  getSheetUploadCleanPrefs,
  readSheetUploadCleanPrefsFromHost,
  renderSheetUploadCleanControlsHtml,
} from "@/character/sheet-upload-clean";
import { downscaleSheetDataUrlIfOverscaled } from "@/character/sheet-scale-down";
import { mountDraftMotionPreview } from "@/character/character-preview";
import { mountSheetUploadPreview } from "@/character/sheet-upload-preview";
import { downloadCharacterTemplate } from "@/character/template";
import { signOutUser } from "@/auth/google-auth";

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

const MOTION_HEX_ICON: Record<CharacterActionKey, string> = {
  idle: "◇",
  run: "»",
  sit: "▪",
  attack: "✦",
};

export function mountCharacterCreatePage(root: HTMLElement, user: User): () => void {
  const existing = loadCharacterProfile(user.uid);
  const canGm = userCanAccessGmMode(user);
  const mode = loadGameAccessMode(user.uid, canGm);
  const perms = permissionsForGameMode(mode, canGm);
  if (existing && !perms.canRecreateCharacter) {
    window.location.replace("/home?character=1");
    return () => {};
  }

  const isFirstCreate = !existing;

  root.className = "page-shell page-character page-character-create";
  const draft: CharacterDraft = createEmptyDraft();
  let sheetSourceDataUrl: string | null = null;
  let stopPreview: (() => void) | undefined;
  let stopStagePreview: (() => void) | undefined;
  let unbindClean: (() => void) | undefined;
  let sheetModalOpen = false;
  let howToModalOpen = false;
  let createModalOpen = false;
  let onCreateModalKeydown: ((event: KeyboardEvent) => void) | null = null;
  let stagePreviewMotion: CharacterActionKey = "idle";

  const motionStageIcons = (hasSheet: boolean) =>
    CHARACTER_ACTIONS
      .map((action) => {
        const active = stagePreviewMotion === action.key;
        return `<button
          type="button"
          class="character-create-motion-icon${active ? " is-active" : ""}"
          data-stage-motion="${action.key}"
          title="ทดลอง ${escapeHtml(action.label)}"
          aria-label="ทดลอง ${escapeHtml(action.templateLabel)}"
          ${hasSheet ? "" : "disabled"}
        >
          <span class="character-create-motion-icon__glyph" aria-hidden="true">${MOTION_HEX_ICON[action.key]}</span>
          <span class="character-create-motion-icon__label">${escapeHtml(action.templateLabel)}</span>
        </button>`;
      })
      .join("");

  const mountPreviewIfNeeded = () => {
    stopPreview?.();
    stopPreview = undefined;
    if (!draft.spritesheetDataUrl || !sheetModalOpen) return;
    const host = root.querySelector<HTMLElement>("#sheet-preview-host");
    if (!host) return;
    stopPreview = mountSheetUploadPreview(
      host,
      draft.spritesheetDataUrl,
      draft.gridTuning,
      (gridTuning) => {
        draft.gridTuning = gridTuning;
        mountStageCharacterPreview();
      },
      {
        sourceDataUrl: sheetSourceDataUrl ?? draft.spritesheetDataUrl,
        onSheetDataUrlChange: (url) => {
          draft.spritesheetDataUrl = url;
        },
        compactToolbar: true,
        hideCleanControls: true,
      },
    );
  };

  const syncSheetModal = () => {
    const modal = root.querySelector<HTMLElement>("#character-create-sheet-modal");
    if (!modal) return;
    const show = Boolean(draft.spritesheetDataUrl && sheetModalOpen);
    modal.hidden = !show;
    modal.setAttribute("aria-hidden", show ? "false" : "true");
    if (show) {
      mountPreviewIfNeeded();
      ensureCreateModalKeydown();
    } else {
      stopPreview?.();
      stopPreview = undefined;
    }
  };

  const mountStageCharacterPreview = () => {
    stopStagePreview?.();
    stopStagePreview = undefined;
    const host = root.querySelector<HTMLElement>("#character-create-stage-char");
    if (!host || !draft.spritesheetDataUrl) return;
    const action = CHARACTER_ACTIONS.find((a) => a.key === stagePreviewMotion) ?? CHARACTER_ACTIONS[0];
    stopStagePreview = mountDraftMotionPreview(
      host,
      draft.spritesheetDataUrl,
      draft.gridTuning,
      action.row,
      action.label,
      { displaySizePx: 192 },
    );
  };

  const openSheetModal = () => {
    if (!draft.spritesheetDataUrl) return;
    sheetModalOpen = true;
    syncSheetModal();
  };

  const closeSheetModal = () => {
    sheetModalOpen = false;
    createModalOpen = false;
    syncSheetModal();
    syncCreateModal();
    mountStageCharacterPreview();
  };

  const ensureCreateModalKeydown = () => {
    if (onCreateModalKeydown) return;
    onCreateModalKeydown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (howToModalOpen) closeHowToModal();
      else if (sheetModalOpen) closeSheetModal();
      else if (createModalOpen) closeCreateModal();
    };
    window.addEventListener("keydown", onCreateModalKeydown);
  };

  const syncHowToModal = () => {
    const modal = root.querySelector<HTMLElement>("#character-create-howto-modal");
    if (!modal) return;
    modal.hidden = !howToModalOpen;
    modal.setAttribute("aria-hidden", howToModalOpen ? "false" : "true");
    if (howToModalOpen) {
      ensureCreateModalKeydown();
      syncQuestSteps();
    }
  };

  const openHowToModal = () => {
    howToModalOpen = true;
    syncHowToModal();
  };

  const closeHowToModal = () => {
    howToModalOpen = false;
    syncHowToModal();
  };

  const syncCreateModal = () => {
    const modal = root.querySelector<HTMLElement>("#character-create-form-modal");
    if (!modal) return;
    modal.hidden = !createModalOpen;
    modal.setAttribute("aria-hidden", createModalOpen ? "false" : "true");
    if (createModalOpen) {
      ensureCreateModalKeydown();
      unbindClean?.();
      unbindClean = undefined;
      const cleanHost = root.querySelector<HTMLElement>("#create-sheet-upload-clean");
      if (cleanHost) unbindClean = bindSheetUploadCleanControls(cleanHost);
    }
  };

  const openCreateModal = () => {
    createModalOpen = true;
    syncCreateModal();
  };

  const closeCreateModal = () => {
    createModalOpen = false;
    syncCreateModal();
  };

  const questStepClass = (step: 1 | 2 | 3): string => {
    const hasSheet = Boolean(draft.spritesheetDataUrl);
    const ready = isDraftComplete(draft);
    if (step === 1) return hasSheet ? "is-done" : "is-active";
    if (step === 2) {
      if (hasSheet) return "is-done";
      return "is-pending";
    }
    if (ready) return "is-done";
    if (hasSheet) return "is-active";
    return "is-pending";
  };

  const renderQuestStepsHtml = () => {
    const steps = [
      { n: 1 as const, icon: "▣", text: "โหลดเทมเพลต" },
      { n: 2 as const, icon: "⊞", text: `วางสไปรต์ ${GRID_COLUMNS} เฟรม/แถว` },
      { n: 3 as const, icon: "✦", text: "สร้าง" },
    ];
    return steps
      .map(
        (step, index) => `
        <li class="character-create-quest-step ${questStepClass(step.n)}" data-quest-step="${step.n}">
          <span class="character-create-quest-step__badge" aria-hidden="true">${step.n}</span>
          <div class="character-create-quest-step__content">
            <span class="character-create-quest-step__icon" aria-hidden="true">${step.icon}</span>
            <span class="character-create-quest-step__text">${escapeHtml(step.text)}</span>
          </div>
        </li>
        ${index < steps.length - 1 ? '<li class="character-create-quest-connector" aria-hidden="true"></li>' : ""}`,
      )
      .join("");
  };

  const render = () => {
    const canSubmit = isDraftComplete(draft);
    const previewBlock = draft.spritesheetDataUrl
      ? `<div class="character-create-stage-tap-shell">
          <button
            type="button"
            class="character-create-stage-tap-bg"
            id="open-sheet-modal"
            aria-label="ปรับกริด"
          ></button>
          <button
            type="button"
            class="character-create-stage-char-hit"
            id="open-create-modal"
            aria-label="อัปโหลดชีต"
          >
            <div id="character-create-stage-char" class="character-create-stage-char"></div>
          </button>
        </div>`
      : `<button type="button" class="character-create-stage-empty-cta" id="open-create-modal">
          สร้าง
        </button>`;

    root.innerHTML = `
      <main class="character-create-frame" aria-labelledby="create-title">
        <div class="character-create-scene-bg" aria-hidden="true"></div>

        <header class="character-create-topbar">
          ${
            isFirstCreate
              ? `<button
                  type="button"
                  class="character-create-hex-nav character-create-hex-nav--logout"
                  id="create-sign-out"
                  aria-label="ออกจากระบบ"
                >ออก</button>`
              : `<a class="character-create-hex-nav" href="/home?character=1" aria-label="กลับ">‹</a>`
          }
          <div class="character-create-title-wrap">
            <h1 id="create-title" class="character-create-title">สร้างตัวละคร</h1>
            <p class="character-create-brand">Mind Survival</p>
          </div>
          <button
            type="button"
            class="character-create-hex-nav"
            id="open-how-to-modal"
            aria-label="วิธีใช้และขั้นตอน"
          >?</button>
        </header>

        <div class="character-create-stage-layout">
          <section class="character-create-stage" aria-label="ตัวอย่างสไปรต์ชีต">
            <div class="character-create-stage-frame">
              <div class="character-create-stage-glow" aria-hidden="true"></div>
              <div class="character-create-pedestal">
                <div class="character-create-preview-zone character-create-preview-zone--stage">
                  ${previewBlock}
                </div>
              </div>
            </div>
            <p class="character-create-stage-spec">
              ${GRID_ROWS} แถว × ${GRID_COLUMNS} เฟรม · ${TEMPLATE_SHEET_WIDTH}×${TEMPLATE_SHEET_HEIGHT} px
            </p>
            <div class="character-create-motions character-create-motions--stage" role="toolbar" aria-label="ทดลองแอนิเมชัน">
              ${motionStageIcons(Boolean(draft.spritesheetDataUrl))}
            </div>
          </section>

        </div>

        <footer class="character-create-footer" aria-label="บันทึกตัวละคร">
          <p id="create-error" class="form-error character-create-error" role="alert" hidden></p>
          <label class="character-create-name-field character-create-name-field--main">
            <span class="character-create-name-icon" aria-hidden="true">✎</span>
            <input
              type="text"
              id="character-name"
              class="character-create-name-input"
              maxlength="32"
              placeholder="ชื่อตัวละคร"
              value="${escapeHtml(draft.name)}"
              autocomplete="off"
            />
          </label>
          <button type="button" id="create-submit" class="character-create-confirm character-create-confirm--main" ${canSubmit ? "" : "disabled"}>
            <span class="character-create-confirm__shine" aria-hidden="true"></span>
            สร้าง
          </button>
        </footer>

        <div
          id="character-create-form-modal"
          class="character-create-form-modal"
          hidden
          aria-hidden="true"
        >
          <button
            type="button"
            class="character-create-form-modal-backdrop"
            data-close-create-modal
            aria-label="ปิด"
          ></button>
          <div
            class="character-create-form-modal-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-form-modal-title"
          >
            <header class="character-create-form-modal-head">
              <h2 id="create-form-modal-title" class="character-create-form-modal-title">
                อัปโหลดชีต
              </h2>
              <button
                type="button"
                class="grid-icon-btn character-create-form-modal-close"
                data-close-create-modal
                aria-label="ปิด"
              >✕</button>
            </header>
            <div class="character-create-form-modal-body">
              <div class="character-create-dock-inner character-create-dock-inner--modal">
                <div class="character-create-dock-tools" id="create-sheet-upload-clean">
                  ${renderSheetUploadCleanControlsHtml("create-clean")}
                </div>

                <div class="character-create-dock-actions character-create-dock-actions--modal">
                  <label class="character-create-upload-pill sheet-upload">
                    <span aria-hidden="true">↑</span>
                    อัปโหลด
                    <input type="file" id="sheet-file" accept="image/png,image/jpeg,image/webp" hidden />
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          id="character-create-sheet-modal"
          class="character-create-sheet-modal"
          hidden
          aria-hidden="true"
        >
          <button
            type="button"
            class="character-create-sheet-modal-backdrop"
            data-close-sheet-modal
            aria-label="ปิด"
          ></button>
          <div
            class="character-create-sheet-modal-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-sheet-modal-title"
          >
            <header class="character-create-sheet-modal-head">
              <h2 id="create-sheet-modal-title" class="character-create-sheet-modal-title">
                กริดและแอนิเมชัน
              </h2>
              <button
                type="button"
                class="grid-icon-btn character-create-sheet-modal-close"
                data-close-sheet-modal
                aria-label="ปิด"
              >✕</button>
            </header>
            <div id="sheet-preview-host" class="character-create-sheet-modal-body"></div>
            <footer class="character-create-sheet-modal-foot">
              <button type="button" class="character-create-sheet-modal-done" data-close-sheet-modal>
                เสร็จ
              </button>
            </footer>
          </div>
        </div>

        <div
          id="character-create-howto-modal"
          class="character-create-howto-modal"
          hidden
          aria-hidden="true"
        >
          <button
            type="button"
            class="character-create-howto-modal-backdrop"
            data-close-howto-modal
            aria-label="ปิด"
          ></button>
          <div
            class="character-create-howto-modal-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-howto-modal-title"
          >
            <div class="character-create-guide-panel character-create-guide-panel--modal">
              <header class="character-create-guide-head">
                <span class="character-create-guide-crest" aria-hidden="true">◆</span>
                <h2 id="create-howto-modal-title" class="character-create-guide-kicker">ขั้นตอน</h2>
                <button
                  type="button"
                  class="grid-icon-btn character-create-howto-modal-close"
                  data-close-howto-modal
                  aria-label="ปิด"
                >✕</button>
              </header>
              <ol class="character-create-quest-steps">
                ${renderQuestStepsHtml()}
              </ol>
            </div>
            <footer class="character-create-howto-modal-foot">
              <button type="button" id="download-template-howto" class="character-create-howto-download">
                <span aria-hidden="true">▣</span>
                ดาวน์โหลดเทมเพลต
              </button>
              <button type="button" class="character-create-howto-done" data-close-howto-modal>
                เข้าใจแล้ว
              </button>
            </footer>
          </div>
        </div>
      </main>
    `;

    bindHandlers();
    if (!createModalOpen) {
      unbindClean?.();
      unbindClean = undefined;
    }
    syncSheetModal();
    syncHowToModal();
    syncCreateModal();
    mountStageCharacterPreview();
  };

  const showError = (message: string) => {
    const el = root.querySelector<HTMLElement>("#create-error");
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  };

  const bindHandlers = () => {
    const onDownloadTemplate = () => {
      downloadCharacterTemplate();
    };
    root.querySelector("#download-template-howto")?.addEventListener("click", onDownloadTemplate);

    root.querySelector("#create-sign-out")?.addEventListener("click", () => {
      void signOutUser();
    });

    root.querySelector("#open-how-to-modal")?.addEventListener("click", () => {
      openHowToModal();
    });

    root.querySelectorAll("#open-create-modal").forEach((el) => {
      el.addEventListener("click", () => {
        openCreateModal();
      });
    });

    root.querySelectorAll("[data-close-create-modal]").forEach((el) => {
      el.addEventListener("click", () => {
        closeCreateModal();
      });
    });

    root.querySelectorAll("[data-close-howto-modal]").forEach((el) => {
      el.addEventListener("click", () => {
        closeHowToModal();
      });
    });

    const nameInput = root.querySelector<HTMLInputElement>("#character-name");
    nameInput?.addEventListener("input", () => {
      draft.name = nameInput.value;
      updateSubmitState();
    });

    const fileInput = root.querySelector<HTMLInputElement>("#sheet-file");
    fileInput?.addEventListener("change", () => {
      const file = fileInput.files?.[0];
      if (!file) return;
      void (async () => {
        try {
          let dataUrl = await readImageFileAsDataUrl(file);
          const downscaled = await downscaleSheetDataUrlIfOverscaled(dataUrl);
          dataUrl = downscaled.dataUrl;
          const probe = new Image();
          await new Promise<void>((resolve, reject) => {
            probe.onload = () => resolve();
            probe.onerror = () => reject(new Error("โหลดภาพไม่สำเร็จ"));
            probe.src = dataUrl;
          });
          sheetLayoutFromImage(probe);
          sheetSourceDataUrl = dataUrl;
          const cleanHost = root.querySelector<HTMLElement>("#create-sheet-upload-clean");
          const cleanPrefs = cleanHost
            ? readSheetUploadCleanPrefsFromHost(cleanHost)
            : getSheetUploadCleanPrefs();
          draft.spritesheetDataUrl = await applySheetUploadClean(dataUrl, cleanPrefs);
          draft.gridTuning = gridTuningForImageSize(
            probe.naturalWidth,
            probe.naturalHeight,
            supportsTemplateGridLayout,
          );
          const errorEl = root.querySelector<HTMLElement>("#create-error");
          errorEl?.setAttribute("hidden", "true");
          createModalOpen = false;
          sheetModalOpen = true;
          render();
        } catch (err) {
          draft.spritesheetDataUrl = null;
          sheetSourceDataUrl = null;
          draft.gridTuning = defaultGridTuning();
          const message =
            err instanceof Error ? err.message : "อัปโหลดไม่สำเร็จ ลองใหม่อีกครั้ง";
          showError(message);
          fileInput.value = "";
          updateSubmitState();
        }
      })();
    });

    root.querySelector("#create-submit")?.addEventListener("click", (event) => {
      const button = event.currentTarget as HTMLButtonElement;
      void onSubmit(button);
    });

    root.querySelector("#open-sheet-modal")?.addEventListener("click", () => {
      openSheetModal();
    });

    root.querySelectorAll<HTMLButtonElement>("[data-stage-motion]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.stopPropagation();
        if (!draft.spritesheetDataUrl || button.disabled) return;
        const key = button.dataset.stageMotion as CharacterActionKey | undefined;
        if (!key) return;
        stagePreviewMotion = key;
        root
          .querySelectorAll(".character-create-motion-icon")
          .forEach((el) => el.classList.remove("is-active"));
        button.classList.add("is-active");
        mountStageCharacterPreview();
      });
    });

    root.querySelectorAll("[data-close-sheet-modal]").forEach((el) => {
      el.addEventListener("click", () => {
        closeSheetModal();
      });
    });
  };

  const syncQuestSteps = () => {
    root.querySelectorAll<HTMLElement>("[data-quest-step]").forEach((el) => {
      const n = Number(el.dataset.questStep);
      if (n !== 1 && n !== 2 && n !== 3) return;
      el.classList.remove("is-active", "is-done", "is-pending");
      el.classList.add(questStepClass(n));
    });
  };

  const updateSubmitState = () => {
    const submit = root.querySelector<HTMLButtonElement>("#create-submit");
    if (!submit) return;
    submit.disabled = !isDraftComplete(draft);
    syncQuestSteps();
  };

  const onSubmit = async (button: HTMLButtonElement) => {
    if (!isDraftComplete(draft) || !draft.spritesheetDataUrl) return;
    button.disabled = true;
    const errorEl = root.querySelector<HTMLElement>("#create-error");
    errorEl?.setAttribute("hidden", "true");

    try {
      let sheetUrl = draft.spritesheetDataUrl;
      if (sheetSourceDataUrl) {
        const cleanHost = root.querySelector<HTMLElement>("#create-sheet-upload-clean");
        const cleanPrefs = cleanHost
          ? readSheetUploadCleanPrefsFromHost(cleanHost)
          : getSheetUploadCleanPrefs();
        sheetUrl = await applySheetUploadClean(sheetSourceDataUrl, cleanPrefs);
      }
      const parsed = await parseCharacterSpritesheet(
        sheetUrl!,
        draft.gridTuning,
      );
      saveCharacterProfile(user.uid, {
        version: 2,
        name: draft.name.trim(),
        createdAt: Date.now(),
        spritesheetDataUrl: parsed.spritesheetDataUrl,
        grid: parsed.grid,
        actions: parsed.actions,
      });
      createModalOpen = false;
      window.location.assign("/home");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "บันทึกไม่สำเร็จ ลองใหม่อีกครั้ง";
      showError(message);
      button.disabled = false;
    }
  };

  render();
  return () => {
    if (onCreateModalKeydown) {
      window.removeEventListener("keydown", onCreateModalKeydown);
      onCreateModalKeydown = null;
    }
    unbindClean?.();
    stopPreview?.();
    stopStagePreview?.();
    root.replaceChildren();
  };
}
